import moment from "moment-timezone";

/**
 * Format a given date to a specific date format in a given timezone
 * @param {number} date - The date in Unix timestamp format
 * @param {string} timezone - The timezone to format the date in
 * @returns {string} - The formatted date string
 */
export function formatDate(date, timezone) {
    return moment
        .unix(date)
        .tz(timezone)
        .format("DD MMM YYYY");
}

/**
 * Format a given date to a specific time format in a given timezone
 * @param {number} date - The date in Unix timestamp format
 * @param {string} timezone - The timezone to format the date in
 * @returns {string} - The formatted time string
 */
export function formatTime(date, timezone) {
    return moment
        .unix(date)
        .tz(timezone)
        .format("HH:mm (hh:mm A)");
}

/**
 * Get the formatted pickup date in the specified timezone
 * @param {number} date - The date in Unix timestamp format
 * @param {string} timezone - The timezone to format the date in
 * @returns {string} - The formatted date string
 */
export function getPickupDate(date, timezone) {
    return moment
        .unix(date)
        .tz(timezone)
        .format("YYYY-MM-DD");
}

/**
 * Get the formatted pickup time in the specified timezone
 * @param {number} date - The date in Unix timestamp format
 * @param {string} timezone - The timezone to format the date in
 * @returns {string} - The formatted time string
 */
export function getPickupTime(date, timezone) {
    return moment
        .unix(date)
        .tz(timezone)
        .format("hh:mm A");
}

/**
 * Generate a message to inform the user about flight adjustment
 * @param {string} flightType - The type of flight (ARRIVAL or DEPARTURE)
 * @returns {string} - The generated message
 */
export function generateMessageToFlightAdjustment(flightType) {
    if (flightType === "ARRIVAL") {
        return "You have updated your pick-up date/time based on the selected flight’s arrival time.";
    } else if (flightType === "DEPARTURE") {
        return "You have updated your pick-up date based on the selected flight’s departure time.";
    }
    return "You have updated your pick-up date/time based on the selected flight's schedule";
}

/**
 * Get a notification message based on the flight type
 * @param {string} flightType - The type of flight (ARRIVAL or DEPARTURE)
 * @returns {string} - The notification message
 */
export function generateMessageToNotify(flightType) {
    if (flightType === "ARRIVAL") {
        return "Pick-up date/time updated based on selected flight arrival.";
    } else if (flightType === "DEPARTURE") {
        return "Pick-up date updated based on selected flight departure.";
    }
    return "Pick-up date/time updated based on selected flight schedule.";
}

/**
 * Filter out duplicate flights based on departure time
 * @param {Array} flights - The array of flight objects
 * @returns {Array} - The array of unique flight objects
 */
export function filterUniqueFlights(flights = []) {
    return flights.filter((flight, index, self) => {
        return (
            self
                .map(element => element.departuretime)
                .indexOf(flight.departuretime) === index
        );
    });
}

/**
 * Generate a validation message for airport mismatch based on flight type
 * @param {string} flightType - The type of flight (ARRIVAL or DEPARTURE)
 * @returns {string} - The validation message
 */
export function generateValidateAirportMessage(flightType) {
    if (flightType === "ARRIVAL") {
        return "The selected flight doesn’t match your arrival airport. Please confirm the flight number or airport.";
    } else if (flightType === "DEPARTURE") {
        return "The selected flight doesn’t match your departure airport. Please confirm the flight number or airport.";
    }
    return "The selected flight doesn’t match your airport. Please confirm the flight number or airport.";
}